import React, { useContext, useEffect, useRef, useState } from 'react';
import { ContextProvider } from '../../root/TopLevelContext';
import styles from './otpInput.module.scss';

// A row of single-digit boxes (auto-advance on type, backspace to previous,
// paste-fill) for "Enter your code" (SecureAccount.ts, generateSignInWithCodeModal)
// — dispatches {id, value} on every change, same shape Input's onChange
// dispatch uses, where `value` is the digits typed so far (not just once
// complete).
const OtpInput = ({ id, length = 5, action = {}, autoFocus = false }) => {
  const { model = String(), trigger = String() } = action;
  const grandpaContext = useContext(ContextProvider);
  const [digits, setDigits] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus) inputRefs.current[0] && inputRefs.current[0].focus();
    // Mount-only: focusing on every re-render would steal focus away from
    // whichever box the user is actually typing into.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = nextDigits => {
    if (model === 'localActions' && trigger) {
      grandpaContext._getAction(trigger) && grandpaContext._getAction(trigger)({ id, value: nextDigits.join('') });
    }
  };

  const focusBox = index => {
    const box = inputRefs.current[index];
    box && box.focus();
  };

  const handleChange = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    const nextDigits = [...digits];
    nextDigits[index] = digit;
    setDigits(nextDigits);
    dispatch(nextDigits);
    if (digit && index < length - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) focusBox(index - 1);
  };

  const handlePaste = event => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    event.preventDefault();
    const nextDigits = Array(length).fill('');
    pasted.split('').forEach((char, i) => {
      nextDigits[i] = char;
    });
    setDigits(nextDigits);
    dispatch(nextDigits);
    focusBox(Math.min(pasted.length, length) - 1);
  };

  return (
    <div id={id} className={styles.otpInput}>
      {digits.map((digit, index) => (
        // Fixed-length, position-based boxes (never reordered/added/removed),
        // so the index is a genuinely stable identity here, same reasoning
        // as radioInput.jsx's use of the same suppression below.
        <input
          key={`${id}-${index}`} // eslint-disable-line react/no-array-index-key
          ref={el => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          // maxLength=1 blocks typing a replacement digit once a box is
          // already filled (e.g. correcting a wrong code after a failed
          // verify) unless the existing character is selected first —
          // without this, onChange silently never fires and the stale first
          // digit stays in state (and gets sent to the mutation) even though
          // the user thinks they retyped it.
          onFocus={e => e.target.select()}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={styles.otpBox}
        />
      ))}
    </div>
  );
};

export default OtpInput;
